"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCurationGroup } from "@/generated/models/ApiWaveCurationGroup";
import { useWave } from "@/hooks/useWave";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { PlusIcon } from "@heroicons/react/24/solid";
import React, { useContext, useMemo } from "react";
import { Tooltip } from "react-tooltip";
import type { LeaderboardViewMode } from "../types";
import { WaveLeaderboardCurationGroupSelect } from "./WaveLeaderboardCurationGroupSelect";
import { useLeaderboardHeaderControlMeasurements } from "./useLeaderboardHeaderControlMeasurements";
import { WAVE_LEADERBOARD_SORT_ITEMS, WaveleaderboardSort } from "./WaveleaderboardSort";
import { resolveWaveLeaderboardHeaderControlModes } from "./waveLeaderboardHeaderControls";
interface WaveLeaderboardHeaderProps {
  readonly wave: ApiWave;
  readonly onCreateDrop: () => void;
  readonly viewMode: LeaderboardViewMode;
  readonly onViewModeChange: (mode: LeaderboardViewMode) => void;
  readonly sort: WaveDropsLeaderboardSort;
  readonly onSortChange: (sort: WaveDropsLeaderboardSort) => void;
  readonly curationGroups?: readonly ApiWaveCurationGroup[] | undefined;
  readonly curatedByGroupId?: string | null | undefined;
  readonly onCurationGroupChange?: ((groupId: string | null) => void) | undefined;
}
export const WaveLeaderboardHeader: React.FC<WaveLeaderboardHeaderProps> = t0 => {
  const $ = _c(68);
  const {
    wave,
    onCreateDrop,
    viewMode: t1,
    onViewModeChange,
    sort,
    onSortChange,
    curationGroups: t2,
    curatedByGroupId: t3,
    onCurationGroupChange
  } = t0;
  const viewMode = t1 === undefined ? "list" : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const curationGroups = t4;
  const curatedByGroupId = t3 === undefined ? null : t3;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const {
    isMemesWave,
    participation
  } = useWave(wave);
  const showCurationGroupSelect = Boolean(onCurationGroupChange && curationGroups.length > 0);
  const viewModes = isMemesWave ? ["list", "grid"] : ["list", "grid", "grid_content_only"];
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Map(WAVE_LEADERBOARD_SORT_ITEMS.map(_temp));
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const sortLabelByValue = t5;
  let t6;
  if ($[3] !== sort) {
    t6 = sortLabelByValue.get(sort) ?? "Current Vote";
    $[3] = sort;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const activeSortLabel = t6;
  let t7;
  bb0: {
    if (!showCurationGroupSelect || !curatedByGroupId) {
      t7 = "All submissions";
      break bb0;
    }
    let t8;
    if ($[5] !== curatedByGroupId || $[6] !== curationGroups) {
      t8 = curationGroups.find(group => group.id === curatedByGroupId)?.name ?? "All submissions";
      $[5] = curatedByGroupId;
      $[6] = curationGroups;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    t7 = t8;
  }
  const activeCurationLabel = t7;
  let t8;
  if ($[8] !== curationGroups) {
    t8 = curationGroups.map(_temp2).join("|");
    $[8] = curationGroups;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const curationProbeKey = t8;
  const t9 = `${activeSortLabel}|${activeCurationLabel}|${curationProbeKey}`;
  let t10;
  if ($[10] !== showCurationGroupSelect || $[11] !== t9) {
    t10 = {
      showCurationGroupSelect,
      remeasureKey: t9
    };
    $[10] = showCurationGroupSelect;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const {
    controlsRowRef,
    viewModeTabsRef,
    sortTabsProbeRef,
    sortDropdownProbeRef,
    curationTabsProbeRef,
    curationDropdownProbeRef,
    measurements
  } = useLeaderboardHeaderControlMeasurements(t10);
  let t11;
  if ($[13] !== measurements.availableWidth || $[14] !== measurements.curationDropdownWidth || $[15] !== measurements.curationTabsWidth || $[16] !== measurements.sortDropdownWidth || $[17] !== measurements.sortTabsWidth || $[18] !== measurements.viewModesWidth || $[19] !== showCurationGroupSelect) {
    t11 = resolveWaveLeaderboardHeaderControlModes({
      availableWidth: measurements.availableWidth,
      viewModesWidth: measurements.viewModesWidth,
      sortTabsWidth: measurements.sortTabsWidth,
      sortDropdownWidth: measurements.sortDropdownWidth,
      hasCurationControl: showCurationGroupSelect,
      curationTabsWidth: measurements.curationTabsWidth,
      curationDropdownWidth: measurements.curationDropdownWidth
    });
    $[13] = measurements.availableWidth;
    $[14] = measurements.curationDropdownWidth;
    $[15] = measurements.curationTabsWidth;
    $[16] = measurements.sortDropdownWidth;
    $[17] = measurements.sortTabsWidth;
    $[18] = measurements.viewModesWidth;
    $[19] = showCurationGroupSelect;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const controlModes = t11;
  const getViewModeLabel = _temp3;
  const getViewModeTabClass = _temp4;
  const getViewModeIcon = _temp5;
  const t12 = "tw-relative tw-flex tw-flex-col tw-gap-y-4 tw-bg-black tw-@container";
  const t13 = "tw-flex tw-items-start tw-gap-2";
  const t14 = "leaderboard-header-controls-row";
  const t15 = `tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-2 ${controlModes.enableControlsScroll ? "horizontal-menu-hide-scrollbar tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-iron-700/60" : "tw-overflow-x-hidden"}`;
  const t16 = "tablist";
  const t17 = "Leaderboard view modes";
  const t18 = "tw-flex tw-flex-shrink-0 tw-gap-0.5 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-1";
  const t19 = viewModes.map(mode_2 => <React.Fragment key={mode_2}><button type="button" role="tab" aria-label={getViewModeLabel(mode_2)} aria-selected={viewMode === mode_2} tabIndex={viewMode === mode_2 ? 0 : -1} className={getViewModeTabClass(mode_2, viewMode)} onClick={() => onViewModeChange(mode_2)} data-tooltip-id={`leaderboard-view-mode-${mode_2}-${wave.id}`}>{getViewModeIcon(mode_2)}<span className="tw-sr-only">{getViewModeLabel(mode_2)}</span></button><Tooltip id={`leaderboard-view-mode-${mode_2}-${wave.id}`} place="top" offset={8} opacity={1} style={{
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    }}>{getViewModeLabel(mode_2)}</Tooltip></React.Fragment>);
  let t20;
  if ($[21] !== t19 || $[22] !== viewModeTabsRef) {
    t20 = <div ref={viewModeTabsRef} role={t16} aria-label={t17} className={t18}>{t19}</div>;
    $[21] = t19;
    $[22] = viewModeTabsRef;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== controlModes.sortMode || $[25] !== onSortChange || $[26] !== sort) {
    t21 = <div className="tw-flex-shrink-0"><WaveleaderboardSort sort={sort} onSortChange={onSortChange} mode={controlModes.sortMode} /></div>;
    $[24] = controlModes.sortMode;
    $[25] = onSortChange;
    $[26] = sort;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== controlModes.curationMode || $[29] !== curatedByGroupId || $[30] !== curationGroups || $[31] !== onCurationGroupChange || $[32] !== showCurationGroupSelect) {
    t22 = showCurationGroupSelect && onCurationGroupChange && <div className="tw-flex-shrink-0"><WaveLeaderboardCurationGroupSelect groups={curationGroups} selectedGroupId={curatedByGroupId} onChange={onCurationGroupChange} mode={controlModes.curationMode} /></div>;
    $[28] = controlModes.curationMode;
    $[29] = curatedByGroupId;
    $[30] = curationGroups;
    $[31] = onCurationGroupChange;
    $[32] = showCurationGroupSelect;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== controlsRowRef || $[35] !== t15 || $[36] !== t20 || $[37] !== t21 || $[38] !== t22) {
    t23 = <div ref={controlsRowRef} data-testid={t14} className={t15}>{t20}{t21}{t22}</div>;
    $[34] = controlsRowRef;
    $[35] = t15;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== connectedProfile || $[41] !== isMemesWave || $[42] !== onCreateDrop || $[43] !== participation) {
    t24 = connectedProfile && participation.isEligible && <div className={`${isMemesWave ? "lg:tw-hidden" : ""}`}><PrimaryButton loading={false} disabled={false} onClicked={onCreateDrop} padding="tw-px-3 tw-py-2"><PlusIcon className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>Drop</span></PrimaryButton></div>;
    $[40] = connectedProfile;
    $[41] = isMemesWave;
    $[42] = onCreateDrop;
    $[43] = participation;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t23 || $[46] !== t24) {
    t25 = <div className={t13}>{t23}{t24}</div>;
    $[45] = t23;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = WAVE_LEADERBOARD_SORT_ITEMS.map(_temp6);
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== sortTabsProbeRef) {
    t27 = <div ref={sortTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1 tw-rounded-lg tw-bg-iron-950 tw-p-1 tw-ring-1 tw-ring-inset tw-ring-iron-700">{t26}</div>;
    $[49] = sortTabsProbeRef;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="tw-font-semibold tw-text-iron-500">Sort: </span>;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== activeSortLabel || $[53] !== sortDropdownProbeRef) {
    t29 = <div ref={sortDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700">{t28}{activeSortLabel}</div>;
    $[52] = activeSortLabel;
    $[53] = sortDropdownProbeRef;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== activeCurationLabel || $[56] !== curationDropdownProbeRef || $[57] !== curationGroups || $[58] !== curationTabsProbeRef || $[59] !== showCurationGroupSelect) {
    t30 = showCurationGroupSelect && <><div ref={curationTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1"><div className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">All submissions</div>{curationGroups.map(_temp7)}</div><div ref={curationDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700"><span className="tw-font-semibold tw-text-iron-500">Group: </span>{activeCurationLabel}</div></>;
    $[55] = activeCurationLabel;
    $[56] = curationDropdownProbeRef;
    $[57] = curationGroups;
    $[58] = curationTabsProbeRef;
    $[59] = showCurationGroupSelect;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t27 || $[62] !== t29 || $[63] !== t30) {
    t31 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-0 tw-overflow-hidden tw-whitespace-nowrap tw-opacity-0">{t27}{t29}{t30}</div>;
    $[61] = t27;
    $[62] = t29;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t25 || $[66] !== t31) {
    t32 = <div className={t12}>{t25}{t31}</div>;
    $[65] = t25;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  return t32;
};
function _temp(item) {
  return [item.value, item.label];
}
function _temp2(group_0) {
  return `${group_0.id}:${group_0.name}`;
}
function _temp3(mode) {
  if (mode === "list") {
    return "List view";
  }
  if (mode === "grid") {
    return "Grid view";
  }
  return "Content only";
}
function _temp4(mode_0, activeMode) {
  if (activeMode === mode_0) {
    return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-border-primary-500/50 tw-bg-primary-600/20 tw-text-primary-400";
  }
  return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-bg-white/5 desktop-hover:hover:tw-text-iron-300";
}
function _temp5(mode_1) {
  if (mode_1 === "list") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>;
  }
  if (mode_1 === "grid") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>;
  }
  return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h12A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6Zm3.75 3h9m-9 4.5h9m-9 4.5h5.25" /></svg>;
}
function _temp6(item_0) {
  return <div key={item_0.key} className="tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold">{item_0.label}</div>;
}
function _temp7(group_1) {
  return <div key={group_1.id} className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">{group_1.name}</div>;
}